import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Sparkles, Ruler, ArrowUpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SemanticsTabs } from "@/components/semantics/SemanticsTabs";
import { MetricDialog } from "@/components/semantics/MetricDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useSession } from "@/context/AuthContext";
import { DOMAINS, DOMAIN_BADGE, METRIC_STATUS, STATUS_FILTERS, canReviewRole } from "@/lib/semantics";
import { SEMANTICS } from "@/constants/testIds";
import api from "@/lib/api";

export default function SemanticsMetrics() {
  const { active } = useWorkspace();
  const { user } = useSession();
  const canReview = canReviewRole(user?.role);
  const [metrics, setMetrics] = useState(null);
  const [domain, setDomain] = useState("all");
  const [status, setStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    const params = { workspace_id: active.id };
    if (domain !== "all") params.domain = domain;
    if (status !== "all") params.status = status;
    const { data } = await api.get("/semantics/metrics", { params });
    setMetrics(data);
  }, [active, domain, status]);

  useEffect(() => { load(); }, [load]);

  const promote = async (id) => {
    try {
      await api.patch(`/semantics/metrics/${id}`, { status: "active" });
      toast.success("Metric promoted to active");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to promote");
    }
  };

  const onDiscover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post("/semantics/metrics/discover", null, { params: { workspace_id: active.id } });
      toast.success(`Discovered ${data.count} metric${data.count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discovery failed");
    } finally {
      setDiscovering(false);
    }
  };

  return (
    <div data-testid={SEMANTICS.metricsRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Metrics</h1>
              <SemanticsTabs active="metrics" />
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              First-class, governed metrics. Active metrics resolve by name (and synonyms) in chat.
            </p>
          </div>
          {canReview && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid={SEMANTICS.discoverBtn} onClick={onDiscover} disabled={discovering} className="rounded-full border-border/70 text-xs">
                {discovering ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />} Discover
              </Button>
              <Button size="sm" data-testid={SEMANTICS.newMetricBtn} onClick={() => setDialogOpen(true)} className="rounded-full bg-accent text-xs uppercase tracking-[0.14em] text-accent-foreground hover:bg-accent/90">
                <Plus className="mr-2 h-3.5 w-3.5" /> New metric
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Select value={domain} onValueChange={setDomain}>
            <SelectTrigger data-testid={SEMANTICS.metricsDomainFilter} className="h-9 w-[180px] text-xs"><SelectValue placeholder="Domain" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All domains</SelectItem>
              {DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={SEMANTICS.metricsStatusFilter} className="h-9 w-[160px] text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {metrics === null ? (
          <div className="flex items-center justify-center py-24 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
        ) : metrics.length === 0 ? (
          <div data-testid={SEMANTICS.metricsEmpty} className="mt-10 rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-16 text-center">
            <Ruler className="mx-auto h-8 w-8 text-accent" />
            <h2 className="mt-5 text-lg font-semibold">No metrics yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">Create one, or run Discover to promote metrics from your glossary and chat history.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            {metrics.map((m) => {
              const st = METRIC_STATUS[m.status] || METRIC_STATUS.draft;
              return (
                <div key={m.id} data-testid={SEMANTICS.metricRow} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/40 px-4 py-3 transition-colors hover:border-accent/40">
                  <Link to={`/semantics/metrics/${m.id}`} className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{m.name}</span>
                      <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] ${DOMAIN_BADGE[m.domain] || ""}`}>{m.domain}</Badge>
                      <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] ${st.cls}`}>{st.label}</Badge>
                      {m.synonyms?.length > 0 && <span className="text-[10px] text-muted-foreground">aka {m.synonyms.join(", ")}</span>}
                    </div>
                    {m.definition && <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{m.definition}</p>}
                    {m.default_grain?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {m.default_grain.map((g) => <span key={g} className="rounded-full border border-border/50 px-1.5 py-0 text-[9px] text-muted-foreground">{g}</span>)}
                      </div>
                    )}
                  </Link>
                  {canReview && m.status === "draft" && (
                    <Button size="sm" variant="outline" data-testid={SEMANTICS.metricPromote} onClick={() => promote(m.id)} className="rounded-full border-emerald-500/40 text-xs uppercase tracking-[0.12em] text-emerald-400 hover:bg-emerald-500/10">
                      <ArrowUpCircle className="mr-1.5 h-3.5 w-3.5" /> Promote
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <MetricDialog open={dialogOpen} onOpenChange={setDialogOpen} workspaceId={active?.id} metric={null} onSaved={load} />
    </div>
  );
}
